import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ViewChild, InjectionToken, Injectable, NgModule } from '@angular/core';
import { Required, Default } from '@rogers/cdk/common/decorators';
import { Subject } from 'rxjs';
import * as i1 from '@rogers/cdk/common/services';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { isBrowser } from '@rogers/cdk/common/utils';

const defaultImage = `data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==`;
class PictureComponent {
    constructor(_element, cdr, utilsService, windowService) {
        this._element = _element;
        this.cdr = cdr;
        this.utilsService = utilsService;
        this.windowService = windowService;
        this.defaultImage = defaultImage;
        this.shouldLoad = false;
        this.destroy$ = new Subject();
        this.class$ = new Subject();
        this.disableOptimizations = false;
    }
    set src(value) {
        this._src = value;
        this.cdr.markForCheck();
    }
    get src() {
        if (this.shouldLoad || this.disableOptimizations) {
            return this.isContentfulAsset(this._src)
                ? this.buildImageUrl(this._src, 'src')
                : this._src;
        }
        else {
            return defaultImage;
        }
    }
    set mobileSrc(value) {
        this._mobileSrc = value;
        this.cdr.markForCheck();
    }
    get mobileSrc() {
        if (this.shouldLoad || this.disableOptimizations) {
            return this.isContentfulAsset(this._mobileSrc)
                ? this.buildImageUrl(this._mobileSrc, 'mobileSrc')
                : this._mobileSrc;
        }
        else {
            return '';
        }
    }
    get element() {
        return this._element.nativeElement;
    }
    ngOnInit() {
        this.class$.next(this.getClass());
        if (this.windowService.isBrowser()) {
            this.setupResizeObserver();
            this.setupIntersectionObserver();
        }
    }
    ngOnChanges() {
        this.class$.next(this.getClass());
        this.cdr.detectChanges();
    }
    getClass() {
        const classList = [];
        classList.push(this && this.ratio ? 'ratio-' + this.ratio : '');
        classList.push(this && this.ratioSm ? 'ratio-sm-' + this.ratioSm : '');
        classList.push(this && this.ratioMd ? 'ratio-md-' + this.ratioMd : '');
        classList.push(this && this.ratioLg ? 'ratio-lg-' + this.ratioLg : '');
        classList.push(this && this.height ? 'h-' + this.height : '');
        classList.push(this && this.heightSm ? 'h-sm-' + this.heightSm : '');
        classList.push(this && this.heightMd ? 'h-md-' + this.heightMd : '');
        classList.push(this && this.heightLg ? 'h-lg-' + this.heightLg : '');
        classList.push(this && this.variant ? '-' + this.variant : '');
        return classList.join(' ');
    }
    isContentfulAsset(src) {
        return src && src.includes('//images.ctfassets.net/');
    }
    imageFit() {
        return window
            .getComputedStyle(this.img.nativeElement)
            .getPropertyValue('object-fit');
    }
    getHeight(height) {
        // contenful does not support images with width or height less or equal to 1.
        return Math.ceil(height <= 1 ? 2 : height).toString();
    }
    getWidth(width) {
        // contenful does not support images with width or height less or equal to 1.
        return Math.ceil(width <= 1 ? 2 : width).toString();
    }
    buildImageUrl(source, srcType = 'src') {
        if (!this.disableOptimizations &&
            source &&
            source.match(/(http)|\/\//).index === 0) {
            // Don't execute if src is base64 or any value other than url
            const { width, height } = this.element.getBoundingClientRect();
            const url = new URL(source, `${window.location.protocol}//example.com`); // placeholder base url to properly construct URL without protocol
            if (this.autoResize) {
                if (this.ratio !== 'auto') {
                    url.searchParams.set('h', this.getHeight(height));
                    url.searchParams.set('w', this.getWidth(width));
                }
                else {
                    const dimension = this.config?.ratio?.auto[srcType === 'src'
                        ? 'srcContentfulDimension'
                        : 'mobileSrcContentfulDimension'] || 'width';
                    if (dimension === 'width') {
                        url.searchParams.set('w', this.getWidth(width));
                    }
                    else {
                        url.searchParams.set('h', this.getHeight(height));
                    }
                }
            }
            if (this.imageFit() === 'cover') {
                url.searchParams.set('fit', 'fill');
            }
            if (this.windowService.isBrowser()) {
                if (this.utilsService.isWebpSupported()) {
                    // svg converted to webp gets pixilated when rendered
                    // that's why we are omitting svg from being converted
                    if (!this._src.endsWith('.svg')) {
                        url.searchParams.set('fm', 'webp');
                    }
                    url.searchParams.set('q', this.quality?.toString() || '100');
                }
                else if (this._src.endsWith('.gif')) {
                    url.searchParams.set('q', this.quality?.toString() || '100');
                }
            }
            return url.href;
        }
        return source;
    }
    setupResizeObserver() {
        const resizeObs = this.utilsService.getResizeObserver();
        if (!resizeObs) {
            return;
        }
        this.resizeObserver = new resizeObs(this.utilsService.debounce(() => {
            if (!this.destroy$.isStopped) {
                this.cdr.detectChanges();
            }
        }, 250));
        this.resizeObserver.observe(this.innerContainer.nativeElement);
    }
    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.shouldLoad = true;
                    this.cdr.detectChanges();
                    this.intersectionObserver.unobserve(this.img.nativeElement);
                    this.intersectionObserver.disconnect();
                }
            });
        }, { rootMargin: '50%' });
        this.intersectionObserver.observe(this.img.nativeElement);
    }
    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.innerContainer.nativeElement);
            this.resizeObserver.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.unobserve(this.img.nativeElement);
            this.intersectionObserver.disconnect();
        }
        this.class$.complete();
        this.destroy$.next(void 0);
        this.destroy$.complete();
    }
}
PictureComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i1.UtilsService }, { token: i1.WindowService }], target: i0.ɵɵFactoryTarget.Component });
PictureComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: PictureComponent, selector: "ds-picture", inputs: { src: "src", alt: "alt", mobileSrc: "mobileSrc", variant: "variant", ratio: "ratio", ratioSm: "ratioSm", ratioMd: "ratioMd", ratioLg: "ratioLg", height: "height", heightSm: "heightSm", heightMd: "heightMd", heightLg: "heightLg", quality: "quality", autoResize: "autoResize", disableOptimizations: "disableOptimizations", config: "config" }, viewQueries: [{ propertyName: "img", first: true, predicate: ["img"], descendants: true, static: true }, { propertyName: "innerContainer", first: true, predicate: ["innerContainer"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<span\n  class=\"ds-picture d-block ds-no-overflow\"\n  #innerContainer\n  [ngClass]=\"class$ | async\"\n>\n  <picture>\n    <source\n      *ngIf=\"mobileSrc\"\n      media=\"(max-width: 899.9px)\"\n      [srcset]=\"mobileSrc\"\n    />\n    <source media=\"(min-width: 900px)\" [srcset]=\"src\" />\n    <img #img [src]=\"src\" [alt]=\"alt || ''\" />\n  </picture>\n</span>\n", directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i2.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
__decorate([
    Required()
], PictureComponent.prototype, "src", null);
__decorate([
    Default('lifestyle')
], PictureComponent.prototype, "variant", void 0);
__decorate([
    Default('1by1')
], PictureComponent.prototype, "ratio", void 0);
__decorate([
    Default(true)
], PictureComponent.prototype, "autoResize", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ds-picture', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<span\n  class=\"ds-picture d-block ds-no-overflow\"\n  #innerContainer\n  [ngClass]=\"class$ | async\"\n>\n  <picture>\n    <source\n      *ngIf=\"mobileSrc\"\n      media=\"(max-width: 899.9px)\"\n      [srcset]=\"mobileSrc\"\n    />\n    <source media=\"(min-width: 900px)\" [srcset]=\"src\" />\n    <img #img [src]=\"src\" [alt]=\"alt || ''\" />\n  </picture>\n</span>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1.UtilsService }, { type: i1.WindowService }]; }, propDecorators: { src: [{
                type: Input
            }], alt: [{
                type: Input
            }], mobileSrc: [{
                type: Input
            }], variant: [{
                type: Input
            }], ratio: [{
                type: Input
            }], ratioSm: [{
                type: Input
            }], ratioMd: [{
                type: Input
            }], ratioLg: [{
                type: Input
            }], height: [{
                type: Input
            }], heightSm: [{
                type: Input
            }], heightMd: [{
                type: Input
            }], heightLg: [{
                type: Input
            }], quality: [{
                type: Input
            }], autoResize: [{
                type: Input
            }], disableOptimizations: [{
                type: Input
            }], config: [{
                type: Input
            }], img: [{
                type: ViewChild,
                args: ['img', { static: true }]
            }], innerContainer: [{
                type: ViewChild,
                args: ['innerContainer', { static: true }]
            }] } });

const PRELOAD_IMAGES_LIST = new InjectionToken('PRELOAD_IMAGES_LIST');
const preloadImageFactory = (paths) => {
    return () => new Promise((res) => {
        (paths || []).forEach((path) => {
            if (isBrowser()) {
                new Image().src = path;
            }
        });
        res();
    });
};

class PictureService {
    constructor() { }
    async preload(paths) {
        return await preloadImageFactory(paths)();
    }
}
PictureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PictureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class PictureModule {
}
PictureModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PictureModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureModule, declarations: [PictureComponent], imports: [CommonModule], exports: [PictureComponent] });
PictureModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureModule, providers: [PictureService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PictureModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PictureComponent],
                    imports: [CommonModule],
                    exports: [PictureComponent],
                    providers: [PictureService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PRELOAD_IMAGES_LIST, PictureComponent, PictureModule, PictureService, preloadImageFactory };
